
import ProjectMixin from '@/_mixins/project'
import { Component, Prop, Watch } from 'vue-property-decorator'
import { getRequest, postRequest } from '@/plugins/api-axios'
import { Mixins } from 'vue-mixin-decorator'
import { deserialise, serialise } from 'kitsu-core'
import { languages } from '@/types/languages'
import { Debounce } from 'lodash-decorators'
import DialogTemplate from '@/_components/dialog/template/template.vue'
import JsonApiObject from '@/_models/JsonApi'
import PostProjectUser from '@/views/Authorization/dialogs/postProjectUser/postProjectUser.vue'
import Role from '@/views/Authorization/models/role'
import { userClient } from '@/apolloClient'
import { PREFERRED_LANGUAGE_QUERY } from '@/views/Project/ch/components/Form/gql/queries'
import type MixinInterface from '@/_mixins'

@Component({
    mixins: [ProjectMixin],
    components: { DialogTemplate },
})
export default class FormResponseInviteDialog extends Mixins<MixinInterface>(ProjectMixin) {
    @Prop() contractor!: any
    @Prop() searchUsersFn!: Function
    @Prop() formList!: any[]

    selectedForm: any = null
    selectedLanguage = ''
    emailMessage = ''
    activeRevision = null
    selectedUsers: any[] = []
    isSearchingUsers = false
    userSearchTerm = ''
    users: any[] = []
    roles = []

    // loading states
    fetchingFormLanguages = false
    isSendingEmail = false

    get pendingUsers() {
        return this.selectedUsers.filter((user) => !user.user.data)
    }

    get canAddProjectUser() {
        return this.hasPermission(this.allPermissions, [
            'authorization.manage_access',
            'authorization.delegate_access',
        ])
    }

    get projectUserSearchUrl() {
        return `${this.url_authorization}/projectUsers`
    }

    get languages() {
        if (!this.activeRevision?.data?.languages) return []

        return this.activeRevision.data.languages.map((language: string) => {
            let lang = languages.find((lang) => lang['639-2'] === language)
            return {
                text: lang?.['language-name'],
                value: language,
            }
        })
    }

    get isAvailableResponseCompanies() {
        if (!this.selectedForm) return true
        return this.selectedForm.availableResponseCompanies?.data?.find(
            (company: any) => company.id === this.contractor.company.data.id
        )
    }

    async getRoles() {
        if (this.currentProjectId) {
            let res = await Role.getRoles(this.currentProjectId)
            this.roles = res.filter((role) => role.isNormal)
        }
    }

    async addProjectUser() {
        if (!this.canAddProjectUser) return

        this.$refs.userAutocomplete?.blur()
        const data = await JsonApiObject.openDialog(PostProjectUser, {
            project: this.currentProject,
            roles: this.roles,
            forContractor: true,
            contractor: this.contractor,
        })
        this.userSearchTerm = ''
        if (data) {
            const newProjectUser = deserialise(data).data
            this.users.push(newProjectUser)
            this.selectedUsers.push(newProjectUser)
        }
    }

    createContractorFormResponse() {
        const formResponsePayload = serialise('contractorFormResponse', {
            initialLanguageId: this.selectedLanguage,
            company: {
                data: {
                    type: 'company',
                    id: this.contractor.company.data.id,
                },
            },
            form: {
                data: {
                    type: 'contractorForm',
                    id: this.selectedForm.id,
                },
            },
            writeAccess: {
                data: this.selectedUsers.map((user: any) => ({
                    type: 'projectUser',
                    id: user.id,
                })),
            },
        })

        return postRequest(
            `${this.contractorBaseUrl}/contractorFormResponses`,
            this.getRequestHeaders('POST'),
            formResponsePayload.data
        )
    }

    async send() {
        try {
            this.isSendingEmail = true
            let { data } = await this.createContractorFormResponse()
            let payload = serialise('contractorFormResponseEmail', {
                kind: 'respond',
                languageId: this.selectedLanguage,
                message: this.emailMessage,
                response: {
                    data: {
                        type: 'contractorFormResponse',
                        id: data.data.id,
                    },
                },
                recipients: {
                    data: this.selectedUsers.map((user: any) => ({
                        type: 'projectUser',
                        id: user.id,
                    })),
                },
            })
            await postRequest(
                `${this.contractorBaseUrl}/contractorFormResponseEmails`,
                this.getRequestHeaders('POST'),
                payload.data
            )
            this.$emit('close', { refresh: true })
        } finally {
            this.isSendingEmail = false
        }
    }

    onSearchInputChanged(value: string) {
        this.isSearchingUsers = Boolean(value)
    }

    @Watch('userSearchTerm')
    @Debounce(300)
    async searchUsers() {
        if (!this.userSearchTerm) return

        this.isSearchingUsers = true
        const users = await this.searchUsersFn(
            {
                'filter[userKindIfConfirmed]': 'normal',
                ...(this.userSearchTerm.length > 1 && {
                    'filter[searchTerm]': this.userSearchTerm,
                }),
            },
            true /** includeAll */
        )
        this.users = users
        this.users.push(...this.selectedUsers)
        this.isSearchingUsers = false
    }

    @Watch('selectedForm')
    async getActiveFormRevision() {
        if (!this.selectedForm || !this.isAvailableResponseCompanies) return

        try {
            this.fetchingFormLanguages = true
            this.selectedLanguage = ''

            let { data: formData } = await getRequest(
                `${this.formBaseUrl}/forms`,
                this.getRequestHeaders(),
                {
                    'filter[resource]': this.selectedForm.id,
                    include: 'activeRevision',
                }
            )
            let [formRevision] = deserialise(formData).data
            this.activeRevision = formRevision.activeRevision.data
            let preferredLanguage

            if (this.activeRevision?.data?.languages?.length > 1) {
                const preferredLanguageResult = await userClient.query({
                    query: PREFERRED_LANGUAGE_QUERY,
                    variables: {
                        candidates: this.activeRevision.data.languages,
                    },
                    fetchPolicy: 'network-only',
                })
                preferredLanguage = preferredLanguageResult.data?.myPreferredLanguage
            }

            if (preferredLanguage) {
                this.selectedLanguage = preferredLanguage
            } else if (this.activeRevision?.data?.languages?.length === 1) {
                this.selectedLanguage = this.activeRevision.data.languages[0]
            }
        } finally {
            this.fetchingFormLanguages = false
        }
    }

    async created() {
        if (this.formList.length === 1) this.selectedForm = this.formList[0]
        this.getRoles()
    }
}
